import axios, { AxiosError } from "axios";
import {
  CONTENT_TYPE_JSON,
  DEFAULT_TIMEOUT,
  TOKEN_EXPIRED_SECONDS,
  getAppEnv,
} from "../../common/settings";

interface RsiTokenApiRequestBody {
  grant_type: string;
  redirect_uri: string;
  code: string;
  code_verifier: string;
  client_id: string;
  expires_in?: number;
}

interface RsiTokenByRefleshApiRequestBody {
  grant_type: string;
  refresh_token: string;
  client_id: string;
}

interface RsiTokenApiResponseBody {
  access_token: string;
  refresh_token: string;
}

/** トークンAPIの結果です。 */
export interface TokenResult {
  /** アクセストークン */
  accessToken: string;
  /** リフレッシュトークン */
  refreshToken: string;
  /** エラー情報 */
  error?: AxiosError;
}

const appEnv = getAppEnv();

/**
 * トークンを取得します。
 * 
 * @param code - 認証ページが作成したコード
 * @param codeVerifier - CODE_VERIFIER
 * @returns 処理結果。  
 *    処理成功時は accessToke, refreshToken が格納されます。  
 *    エラー発生時は error プロパティにエラー情報が格納されます。  
 *   
 */
export const getToken = async (code: string, codeVerifier: string): Promise<TokenResult> => {
  const requestBody: RsiTokenApiRequestBody = {
    grant_type: "authorization_code",
    redirect_uri: appEnv.baseUrl + "/app/login",
    code: code,
    code_verifier: codeVerifier,
    client_id: appEnv.clientId,
    expires_in: TOKEN_EXPIRED_SECONDS,
  };

  try {
    const response = await axios.post<RsiTokenApiResponseBody>(
      appEnv.authUrl + "/v1/aut/oauth/provider/token",
      requestBody,
      {
        headers: CONTENT_TYPE_JSON,
        timeout: DEFAULT_TIMEOUT,
      },
    );

    return {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
    };
  } catch (e) {
    return {
      accessToken: "",
      refreshToken: "",
      error: e as AxiosError,
    };
  }
};

/**
 * リフレッシュトークンを元にトークンを取得します。
 * 
 * @param refreshToken - リフレッシュトークン
 * @returns 処理結果。  
 *    処理成功時は accessToke, refreshToken が格納されます。  
 *    エラー発生時は error プロパティにエラー情報が格納されます。  
 *   
 */
export const getTokenByRefeshToken = async (refreshToken: string): Promise<TokenResult> => {
  const requestBody: RsiTokenByRefleshApiRequestBody = {
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: appEnv.clientId,
  };

  try {
    const response = await axios.post<RsiTokenApiResponseBody>(
      appEnv.authUrl + "/v1/aut/oauth/provider/token",
      requestBody,
      {
        headers: CONTENT_TYPE_JSON,
        timeout: DEFAULT_TIMEOUT,
      },
    );

    return {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
    };
  } catch (e) {
    return {
      accessToken: "",
      refreshToken: "",
      error: e as AxiosError,
    };
  }
};
